import classNames from 'classnames'
import React, { PropsWithChildren, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@context/SnackbarContext'

import { Tooltip } from '@ui/components/Tooltip/Tooltip'

import { copyToClipboard } from '@utils/navigator.utils'

import styles from './CopyLink.module.scss'

interface CopyLinkProps extends PropsWithChildren {
  disabled?: boolean
  value?: string | number | null
  successMessage?: string
  tooltipText?: string
  onCopy?: () => void
}

export const CopyLink: React.FC<CopyLinkProps> = (props) => {
  const { value, successMessage, tooltipText, onCopy, disabled, children } = props

  const { copy, canCopy } = useCopyToClipboard({ value, successMessage, onCopy })

  const handleClick = (event: React.MouseEvent) => {
    if (!disabled) {
      event.stopPropagation()
      canCopy && copy()
    }
  }

  return (
    <Tooltip content={tooltipText} disabled={!tooltipText}>
      <span
        className={classNames(styles.wrapper, { [styles.notClickable]: disabled })}
        onClick={handleClick}
      >
        {children}
      </span>
    </Tooltip>
  )
}

interface UseCopyToClipboardOptions {
  value?: string | number | null
  successMessage?: string
  onCopy?: () => void
}

const useCopyToClipboard = ({ value, successMessage, onCopy }: UseCopyToClipboardOptions = {}) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()

  const copy = useCallback(
    (event?: React.MouseEvent) => {
      if (value) {
        event?.stopPropagation()

        copyToClipboard(value)

        addSnackbar.success({
          message: successMessage || t('Text/link copied!'),
        })
        onCopy?.()
      }
    },
    [value, successMessage]
  )

  return { copy, canCopy: !!value }
}
